import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Star, MapPin, Phone, Mail, Globe, Clock, DollarSign, 
  Calendar, Edit2, Camera, ArrowLeft, CheckCircle, Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Service {
  id: string;
  name: string;
  price: number;
  unit: string;
  description: string;
}

interface Review {
  id: string;
  rating: number;
  comment: string;
  author: string;
  date: string;
}

export default function SupplierProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    name: 'Servicios Eléctricos García',
    category: 'Electricista',
    logo: null as string | null,
    phone: '+52 55 1234 5678',
    email: 'contacto@electricidadgarcia.com',
    website: 'www.electricidadgarcia.com',
    address: 'Av. Reforma 123, Ciudad de México',
    description: 'Servicios eléctricos profesionales con más de 15 años de experiencia. Especialistas en instalaciones residenciales e industriales.',
    yearsExperience: 15,
    isVerified: true,
    responseTime: '< 30 min',
    availability: 'Lun - Dom: 8:00 - 20:00',
    rating: 4.8,
    reviewCount: 127,
    completedJobs: 342
  });

  const [services, setServices] = useState<Service[]>([
    { id: '1', name: 'Revisión Eléctrica', price: 350, unit: 'visita', description: 'Diagnóstico completo del sistema eléctrico' },
    { id: '2', name: 'Instalación de Luminarias', price: 250, unit: 'punto', description: 'Instalación de lámparas y focos' },
    { id: '3', name: 'Cambio de Breakers', price: 180, unit: 'unidad', description: 'Reemplazo de interruptores termomagnéticos' },
    { id: '4', name: 'Instalación de Tomacorrientes', price: 200, unit: 'punto', description: 'Instalación de contactos eléctricos' },
  ]);

  const [reviews] = useState<Review[]>([
    { id: '1', rating: 5, comment: 'Excelente servicio, muy profesional y puntual.', author: 'Ana R.', date: '2026-03-15' },
    { id: '2', rating: 5, comment: 'Resolvió el problema rápidamente. Recomendado.', author: 'Carlos M.', date: '2026-03-10' },
    { id: '3', rating: 4, comment: 'Buen trabajo, precio justo.', author: 'María S.', date: '2026-03-05' },
  ]);

  const handleSave = () => {
    setIsEditing(false);
    alert('✅ Perfil actualizado');
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-[#2D3748]">Mi Perfil de Proveedor</h1>
          </div>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl flex items-center gap-2">
              <Edit2 className="w-4 h-4" /> Editar
            </button>
          ) : (
            <button onClick={handleSave} className="px-4 py-2 bg-emerald-500 text-white font-bold rounded-xl flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Guardar
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="relative">
              <div className="w-32 h-32 bg-[#36CCD8]/10 rounded-2xl flex items-center justify-center">
                {profile.logo ? (
                  <img src={profile.logo} alt="Logo" className="w-full h-full object-cover rounded-2xl" />
                ) : (
                  <User className="w-16 h-16 text-[#36CCD8]" />
                )}
              </div>
              {isEditing && (
                <button className="absolute bottom-0 right-0 w-10 h-10 bg-[#36CCD8] text-white rounded-full flex items-center justify-center shadow-lg">
                  <Camera className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-3">
                  <input 
                    type="text" 
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    className="text-2xl font-bold text-[#2D3748] w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                  <input 
                    type="text" 
                    value={profile.category}
                    onChange={(e) => setProfile({...profile, category: e.target.value})}
                    className="text-[#36CCD8] font-medium w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-[#2D3748]">{profile.name}</h2>
                  <p className="text-[#36CCD8] font-medium">{profile.category}</p>
                </>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-amber-400 fill-current" />
                  <span className="font-bold text-[#2D3748]">{profile.rating}</span>
                </div>
                <span className="text-gray-500">({profile.reviewCount} reseñas)</span>
                {profile.isVerified && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Verificado
                  </span>
                )}
              </div>

              <p className="text-gray-500 mt-4">{profile.description}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#2D3748]">{profile.yearsExperience}</p>
              <p className="text-sm text-gray-500">Años de experiencia</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#2D3748]">{profile.completedJobs}</p>
              <p className="text-sm text-gray-500">Trabajos completados</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#2D3748]">{profile.responseTime}</p>
              <p className="text-sm text-gray-500">Tiempo de respuesta</p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <h3 className="font-bold text-[#2D3748] mb-4">Información de Contacto</h3>
          {isEditing ? (
            <div className="space-y-3">
              <input type="tel" value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" placeholder="Teléfono" />
              <input type="email" value={profile.email} onChange={(e) => setProfile({...profile, email: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" placeholder="Email" />
              <input type="text" value={profile.website} onChange={(e) => setProfile({...profile, website: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" placeholder="Website" />
              <input type="text" value={profile.address} onChange={(e) => setProfile({...profile, address: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" placeholder="Dirección" />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-[#2D3748]"><Phone className="w-5 h-5 text-gray-400" /> {profile.phone}</p>
              <p className="flex items-center gap-2 text-[#2D3748]"><Mail className="w-5 h-5 text-gray-400" /> {profile.email}</p>
              <p className="flex items-center gap-2 text-[#2D3748]"><Globe className="w-5 h-5 text-gray-400" /> {profile.website}</p>
              <p className="flex items-center gap-2 text-[#2D3748]"><MapPin className="w-5 h-5 text-gray-400" /> {profile.address}</p>
            </div>
          )}
        </div>

        {/* Services */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#2D3748]">Servicios y Precios</h3>
            {isEditing && (
              <button className="px-3 py-1 bg-[#36CCD8]/10 text-[#36CCD8] font-medium rounded-lg text-sm">
                + Agregar
              </button>
            )}
          </div>
          <div className="space-y-3">
            {services.map(service => (
              <div key={service.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-bold text-[#2D3748]">{service.name}</p>
                  <p className="text-sm text-gray-500">{service.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#36CCD8]">${service.price}</p>
                  <p className="text-sm text-gray-500">/{service.unit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-bold text-[#2D3748] mb-4">Reseñas Recientes</h3>
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.id} className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#2D3748]">{review.author}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-current' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-gray-400">{review.date}</span>
                </div>
                <p className="text-gray-600">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
